<script setup>
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import {
  computed, onBeforeMount, onMounted, ref, toValue,
} from 'vue';
import Trans from '@/i18n/translation';
import { useGlobalState, hasCompanyPlan } from '@/store/user.store';
import { useSubscriptionPolling } from '@/services/subscription.js';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { fetchPlans } from '@/services/plans.js';
import { fetchAndUpdateUserState } from '@/services/authentication.js';
import {useRoute, useRouter} from "vue-router";
import { trackEvent } from '@/services/analytics/googleAnalytics.js';

const props = defineProps({
  paymentSuccess: {
    required: true,
    default: false,
  },
  freePaymentSuccess: {
    required: false,
    default: false,
  },
});

const chosenPlan = ref(null);
const { fetch: updateUserState } = fetchAndUpdateUserState();
const { isAuthenticated, userState } = useGlobalState();
const subscriptionPolling = ref(null);


onMounted(async () => {
  await fetchPlans();
  await updateUserState();
  const storedPlan = sessionStorage.getItem('chosenPlan');
  if (storedPlan) {
    chosenPlan.value = JSON.parse(storedPlan);
  } else {
    console.error('No chosen plan found after PayPal redirection');
  }
  const endpointUsed = computed(() => (hasCompanyPlan(toValue(userState)) ? 'subscription' : 'mySubscription'));
  subscriptionPolling.value = useSubscriptionPolling(props.paymentSuccess, endpointUsed);

  trackEvent('register_confirmation_step', {
    paymentSuccess: props.paymentSuccess,
    event_category: ['Payments', 'Register'],
    event_label: 'Confirmation Step',
  });
  if (props.freePaymentSuccess) {
    trackEvent('confirmation_free_payment_success', {
      event_category: ['Payments', 'Register'],
      event_label: chosenPlan.value || 'unknown',
    });
  } else if (props.paymentSuccess) {
    trackEvent('confirmation_payment_success', {
      event_category: ['Payments', 'Register'],
      event_label: chosenPlan.value || 'unknown',
    });
  } else {
    trackEvent('confirmation_payment_failed', {
      event_category: ['Payments', 'Register'],
      event_label: chosenPlan.value || 'unknown',
    });
  }
});

const { t } = useI18n();

// eslint-disable-next-line max-len
const isSubscriptionActive = computed(() => (subscriptionPolling.value ? subscriptionPolling.value.isSubscriptionActive : false));

const titleMessage = computed(() => {
  if (props.freePaymentSuccess) return t('registerView.confirmation.successTitle');
  if (props.paymentSuccess) {
    return isSubscriptionActive.value
      ? t('registerView.confirmation.successTitle')
      : t('registerView.confirmation.waitingTitle');
  }
  return t('registerView.confirmation.failureTitle');
});

const infoMessage = computed(() => {
  if (props.freePaymentSuccess) return t('registerView.confirmation.freeInfo', { planName: chosenPlan.value });
  if (props.paymentSuccess) {
    return isSubscriptionActive.value
      ? t('registerView.confirmation.successInfo', { planName: chosenPlan.value })
      : t('registerView.confirmation.waitingInfo', { planName: chosenPlan.value });
  }
  return t('registerView.confirmation.failureInfo');
});

const homeNavigation = Trans.i18nRoute({ name: 'home' });
const loginNavigation = Trans.i18nRoute({ name: 'login' });
const dashboardNavigation = Trans.i18nRoute({ name: 'dashboard' });
const route = useRoute();
const router = useRouter();

function handleDashboardClick() {
  if (route.fullPath.match(/\/[a-z]{2}\/dashboard(\/|$)/)) {
    window.location.href = window.location.href;
  } else {
    router.push(dashboardNavigation);
  }
}

</script>

<template>
  <div
    class="p-12 pt-8 rounded-md bg-basecolor text-primary lg:col-start-2 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg"
  >
    <div class="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0 text-center">
      <div class="text-2xl">
        <template v-if="(paymentSuccess && isSubscriptionActive) || freePaymentSuccess">
          <CheckCircleIcon class="font-semibold w-16 h-16 m-auto" />
        </template>
        <template v-else-if="paymentSuccess && !isSubscriptionActive" />
        <template v-else>
          <XCircleIcon class="font-semibold w-16 h-16 m-auto text-red-500" />
        </template>
      </div>
      <h2 class="text-xl mt-3">
        {{ titleMessage }}
      </h2>

      <p
        class="mt-5 text-sm"
        v-html="infoMessage"
      />

      <div class="mt-10 flex justify-between pt-6">
        <RouterLink
          v-if="(isAuthenticated() && isSubscriptionActive) || !paymentSuccess"
          :to="homeNavigation"
          class="rounded-md border border-primary bg-transparent px-14 py-3 text-sm font-medium text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
        >
          {{ $t('registerView.confirmation.homeButton') }}
        </RouterLink>
        <template v-if="isAuthenticated() && (isSubscriptionActive || freePaymentSuccess)">
          <button
              @click="handleDashboardClick"
              class="rounded-md border border-transparent bg-secondary px-14 py-3 text-sm font-medium text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
          >
            {{ $t('dashboardBtn') }}
          </button>
        </template>
        <template v-else-if="paymentSuccess && !isSubscriptionActive">
          <LoadingSpinner class="w-16 h-16 m-auto" />
        </template>
        <template v-else-if="!isAuthenticated()">
          <RouterLink
            :to="loginNavigation"
            class="rounded-md border border-transparent bg-secondary px-14 py-3 text-sm font-medium text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
          >
            {{ $t('registerView.confirmation.logInButton') }}
          </RouterLink>
        </template>
      </div>
    </div>
  </div>
</template>
